/**
 * ContactForm — config-driven form. Field list and POST endpoint come from
 * config.contact.form.  When the endpoint isn't reachable we still show the
 * success message so the page is usable in standalone preview / demo mode.
 */
import React, { useState } from 'react';
import { COLOR, FONT, RADIUS, baseInputStyle, labelStyle, ELEVATION } from '../../../components/ss/tokens';
import { API_ORIGIN } from '../../../services/apiBase';
import useSiteConfig from '../useSiteConfig';

// Origins the contact form is allowed to POST to: the page's own origin, plus
// the trusted API origin the app was built against (API_ORIGIN). API_ORIGIN is
// a build-time constant, not attacker-controllable, so trusting it does not
// weaken the SSRF guard — it just lets the form reach the dedicated api.<domain>
// host when the API runs on its own origin.
function allowedOrigins() {
  const origins = new Set([window.location.origin]);
  if (API_ORIGIN) {
    try { origins.add(new URL(API_ORIGIN).origin); } catch { /* ignore malformed */ }
  }
  return origins;
}

// The contact-form endpoint is config-supplied (editable in Website Settings).
// An attacker who can write site config must not be able to point the browser
// POST at an arbitrary host (stored SSRF / data exfiltration). Only allow the
// page origin or the trusted API origin (relative paths resolve same-origin).
function isAllowedEndpoint(endpoint) {
  if (typeof endpoint !== 'string' || endpoint.trim() === '') return false;
  try {
    const u = new URL(endpoint, window.location.origin);
    return (
      (u.protocol === 'https:' || u.protocol === 'http:') &&
      allowedOrigins().has(u.origin)
    );
  } catch {
    return false;
  }
}

export default function ContactForm() {
  const { config } = useSiteConfig();
  const form = config.contact?.form;
  const ui = config.ui?.form || {};
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  if (!form) return null;
  const fields = form.fields || [];

  const setVal = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (form.endpoint && !isAllowedEndpoint(form.endpoint)) {
        // Refuse to post to an off-origin / malformed endpoint. Still show the
        // success message (demo-mode posture) but never make the request.
        // eslint-disable-next-line no-console
        console.warn('[contact form] endpoint rejected (not same-origin):', form.endpoint);
      } else if (form.endpoint) {
        const res = await fetch(form.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        // Treat any non-2xx as best-effort; we still show success because the
        // marketing page shouldn't block demo mode behind a working API.
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn('[contact form] endpoint returned', res.status);
        }
      }
      setDone(true);
    } catch (err) {
      // Network errors — same posture: log and continue.
      // eslint-disable-next-line no-console
      console.warn('[contact form] submit failed —', err?.message);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        style={{
          background: COLOR.greenBg,
          border: `1px solid ${COLOR.greenBorder}`,
          borderRadius: RADIUS.lg,
          padding: 32,
          textAlign: 'center',
          color: COLOR.greenDark,
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 12 }} aria-hidden>✓</div>
        <div style={{ fontSize: FONT.size.lg, fontWeight: FONT.weight.semi, marginBottom: 4 }}>
          {ui.successHeading || 'Message received'}
        </div>
        <div style={{ fontSize: FONT.size.md, color: COLOR.greenDark }}>
          {form.successMessage || ui.successDefault || 'Thanks — we\'ll be in touch.'}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{
        background: '#fff',
        border: `1px solid ${COLOR.grayBorder}`,
        borderRadius: RADIUS.lg,
        padding: 28,
        boxShadow: ELEVATION.sm,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {fields.map((f) => {
        const id = `cf-${f.name}`;
        const common = {
          id,
          name: f.name,
          required: f.required,
          value: values[f.name] || '',
          onChange: (e) => setVal(f.name, e.target.value),
          style: baseInputStyle,
        };
        return (
          <div key={f.name}>
            <label htmlFor={id} style={labelStyle}>
              {f.label}
              {f.required && (
                <span
                  style={{ color: COLOR.red, marginLeft: 4 }}
                  aria-label={ui.requiredAriaSuffix || 'required'}
                >
                  {ui.requiredMarker || '*'}
                </span>
              )}
            </label>
            {f.type === 'textarea' ? (
              <textarea {...common} rows={4} style={{ ...baseInputStyle, resize: 'vertical', minHeight: 110 }} />
            ) : f.type === 'select' ? (
              <select {...common}>
                <option value="">{ui.selectPlaceholder || 'Select…'}</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input {...common} type={f.type || 'text'} />
            )}
          </div>
        );
      })}

      {error && (
        <div
          style={{
            background: COLOR.redBg,
            color: COLOR.redDark,
            border: `1px solid ${COLOR.redBorder}`,
            padding: '10px 14px',
            borderRadius: RADIUS.md,
            fontSize: FONT.size.base,
          }}
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        style={{
          background: COLOR.blue,
          color: '#fff',
          border: 'none',
          padding: '14px 24px',
          borderRadius: RADIUS.pill,
          fontSize: FONT.size.md,
          fontWeight: FONT.weight.semi,
          cursor: submitting ? 'progress' : 'pointer',
          opacity: submitting ? 0.7 : 1,
          marginTop: 4,
        }}
      >
        {submitting
          ? (ui.submittingLabel || 'Sending…')
          : (ui.submitLabel || 'Send message')}
      </button>
    </form>
  );
}
